import { Content, ContentStack, ContentTable, ContentText, TDocumentDefinitions } from "pdfmake/interfaces";
import {
  HeaderLogoWithAddress,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";
import FormSymbolImages from "react-lib/apps/common/pdfPrintList/AddSymbolPrintList";

type FormConsentOrderPDPAENProps = {
  detail: Record<string, any>; // -- Type จาก PrintListFormDataHelper
  items: Record<string, any>; // -- Type PatientInfo + additionData จาก PrintListFormDataHelper
};

const FONT_SIZE = 9.5;
const FORM_NAME = "FormConsentOrderPDPAEN";

const FormConsentOrderPDPAEN = async (
  props: FormConsentOrderPDPAENProps,
): Promise<TDocumentDefinitions> => {

  const headerForm = await HeaderLogoWithAddress({
    font: "KanitLM",
    form: FORM_NAME,
    headerMargins: [10, 10, 10, 10],
    logoHeight: 50,
    pageMargins: [15, 70, 15, 30],
    oaDetail: props.detail?.oaDetail,
    division: props?.detail?.division,
    isFullAddress: !!props.detail?.oaDetail,
    isOrderPrintList: true,
    styles: {
      fieldHeader: { alignment: "center", bold: true, fontSize: 11, marginTop: 5 },
      fieldKey: { bold: true },
      fontEN: { font: "PoppinsLM", fontSize: 9.25 },
      tableHeader: { bold: true },
    },
    images: {
      oaLogo: props.detail?.oaDetail?.imageFile || "",
      ...FormSymbolImages,
    },
  });

  const { font, images, styles } = headerForm;

  const fillerValue = (value: string | undefined | null, dottedCount: number): ContentStack => ({
    stack: [
      {
        text: value || " ",
        preserveLeadingSpaces: true,
        alignment: "center",
        marginBottom: -10,
      },
      {
        text: ".".repeat(dottedCount),
      }
    ],
    width: "auto"
  });

  const formatPrice = (value: number): string =>
    value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const packageItems: Record<string, any>[] = props.items?.items || [];

  const packageGrandTotal = packageItems.reduce(
    (sum: number, item: Record<string, any>) => sum + (Number(item?.price) || 0),
    0,
  );

  const packagePurchaseListTable: ContentTable | null =
    packageItems.length === 0
      ? null
      : {
          table: {
            widths: ["80%", "20%"],
            body: [
              [
                { text: "Laboratory Test(s)", bold: true, alignment: "center" },
                { text: "Price", bold: true, alignment: "right" },
              ],
              ...packageItems.map((item: Record<string, any>) => [
                { text: `${item?.product_name_en || ""}`, preserveLeadingSpaces: true },
                { text: formatPrice(Number(item?.price) || 0), alignment: "right" },
              ]),
              [
                { text: "Total Amount", bold: true, alignment: "center" },
                { text: formatPrice(packageGrandTotal), bold: true, alignment: "right" },
              ],
            ],
          },
          layout: {
            hLineWidth: (rowIndex: number, node: ContentTable) =>
              rowIndex === 0 ||
              rowIndex === 1 ||
              rowIndex === node.table.body.length - 1 ||
              rowIndex === node.table.body.length
                ? 1
                : 0,
            vLineWidth: () => 0,
          },
        } as ContentTable;

  return {
    defaultStyle: {
      font,
      fontSize: FONT_SIZE,
      lineHeight: 1,
    },
    pageSize: "A4",
    ...styles,
    ...headerForm,
    images: {
      ...images,
    },
    content: [
      {
        table: {
          widths: ["100%"],
          body: [
            [
              {
                text: "LABORATORY SERVICES CONSENT AND PERSONAL DATA PROTECTION ACKNOWLEDGEMENT FORM",
                bold: true,
                color: "white",
                fillColor: "#333333",
                alignment: "center",
                fontSize: 11,
              }
            ],
            [
              {
                stack: [
                  {
                    columns: [
                      { text: "Full Name : ", width: "auto" },
                      fillerValue(props.items?.full_name_en, 138),
                      { text: "Patient ID : ", width: "auto" },
                      fillerValue(props.detail?.patientHN, 30),
                      { text: "Date of Service: ", width: "auto" },
                      fillerValue(props.items?.formatVisitDate, 65),
                    ],
                    columnGap: 5,
                    marginBottom: -12,
                  },
                  {
                    columns: [
                      { text: "ID Card / Passport No. : ", width: "auto" },
                      fillerValue(props.items?.formatCitizenNo, 100),
                      { text: "Date of Birth : ", width: "auto" },
                      fillerValue(props.items?.birthdate, 60),
                      { text: "Age : ", width: "auto" },
                      fillerValue(props.items?.age, 20),
                      { text: "Yrs", width: "auto" },
                    ],
                    columnGap: 5,
                    marginBottom: -10,
                  },
                  {
                    columns: [
                      { text: "Phone : ", width: "auto" },
                      fillerValue(props.detail?.telMobile, 148),
                      { text: "Email : ", width: "auto" },
                      fillerValue(props.items?.email, 148),
                    ],
                    columnGap: 5,
                    // marginBottom: -10,
                  },
                  {
                    columns: [
                      { text: "| Emergency Contact |", bold: true, width: "auto" },
                      { text: "Full Name : ", width: "auto" },
                      fillerValue(props.items?.relative_address?.owner_name, 105),
                      { text: "Phone : ", width: "auto" },
                      fillerValue(props.items?.relative_address?.tel_mobile, 50),
                      { text: "Relationship : ", width: "auto" },
                      fillerValue(props.items?.relative_address?.relative, 30),
                    ],
                    columnGap: 5,
                    marginBottom: -10,
                  },
                  {
                    columns: [
                      { text: "|", bold: true, width: "auto" },
                      {
                        text: "For Minors / Patients Unable to Provide Consent Independently",
                        italics: true,
                        bold: true,
                        width: "auto"
                      },
                      { text: "|", bold: true, width: "auto" },
                    ],
                    columnGap: 5,
                    // marginBottom: 5,
                  },
                  {
                    columns: [
                      { text: "Legal Representative / Person with Parental Authority : ", noWrap: true, width: "auto" },
                      fillerValue(" ", 160),
                    ],
                    columnGap: 5,
                    marginBottom: -2,
                  },
                  {
                    columns: [
                      { text: "Phone : ", width: "auto" },
                      fillerValue(" ", 139),
                      { text: "Relationship : ", width: "auto" },
                      fillerValue(" ", 140),
                    ],
                    columnGap: 5,
                    // marginBottom: -10,
                  },
                ],
              }
            ]
          ]
        },
        layout: "noBorders",
        // marginBottom: 5,
      },

      // Package Purchase List
      ...(packagePurchaseListTable ? [packagePurchaseListTable as Content] : []),

      // TERMS AND CONDITIONS FOR LABORATORY SERVICES
      {
        table: {
          headerRows: 1,
          widths: ["100%"],
          body: [
            [
              {
                text: "TERMS AND CONDITIONS FOR LABORATORY SERVICES",
                bold: true,
                fillColor: "#9e9e9e",
                marginLeft: 5
              },
            ],
            [
              {
                ul: [
                  { text: `I certify that the health information, medical history, and history of drug or chemical allergies provided by me are true and accurate in all respects. If I conceal or provide false information, the clinic shall not be held liable for any resulting consequences.`, alignment: "justify" },
                  { text: `I consent to a licensed medical technologist collecting specimens (blood / urine / secretions) in accordance with professional standards and acknowledge that there may be minor risks, such as bruising or temporary dizziness, which are known, minor risks associated with the procedure.`, alignment: "justify" },
                  { text: `I understand and accept that laboratory test results are scientific information only and do not constitute a medical diagnosis or treatment by a physician. If I require diagnosis or treatment, I will separately consult a licensed physician.`, alignment: "justify" },
                  { text: `Service fees paid are non-refundable once specimen collection has been completed, except in cases where the clinic is at fault in providing the service. If specimens or tests are required to be referred to an external laboratory, additional charges may apply, and I will be informed in advance.`, alignment: "justify" },
                  { text: `Post-service instructions: Following blood collection, apply pressure to the puncture site for at least 3-5 minutes. Do not massage or rub the arm. If swelling occurs, apply a cold compress and consult a physician if the condition does not improve within 24 hours. For urine testing, avoid foods or medications that may affect the test results. For pregnancy testing, the result must be confirmed by an obstetrician-gynecologist. If any abnormal symptoms occur, seek medical attention immediately and notify the clinic.`, alignment: "justify" },
                  { text: `If the patient is a minor or is unable to provide consent independently, a legal representative, person with parental authority, or other person legally authorized to act on the patient's behalf shall provide consent as required by law.`, alignment: "justify" },
                ]
              }
            ]
          ]
        },
        layout: "noBorders",
      },

      // PREFERRED METHODS FOR RECEIVING TEST RESULTS
      {
        table: {
          headerRows: 1,
          widths: ["100%"],
          body: [
            [
              {
                text: [
                  { text: "PREFERRED METHODS FOR RECEIVING TEST RESULTS", bold: true },
                  { text: " (besides Health Book in LINE Menu)", italics: true, preserveLeadingSpaces: true },
                ],
                fillColor: "#9e9e9e",
                marginLeft: 5
              }
            ],
            [
              {
                columns: [
                  { image: "unchecked", width: 10, marginTop: 2 },
                  { text: "LINE Chat", width: "auto" },
                  { image: "unchecked", width: 10, marginTop: 2 },
                  { text: "Printed Copy at Clinic", width: "auto" },
                  { image: "unchecked", width: 10, marginTop: 2 },
                  { text: `Email : ${".".repeat(210)}`, width: "auto" },
                ],
                columnGap: 5,
                // marginTop: 5,
              }
            ]
          ]
        },
        layout: "noBorders",
      },

      // PDPA
      {
        table: {
          headerRows: 1,
          widths: ["100%"],
          body: [
            [
              {
                text: [
                  { text: "PERSONAL DATA PROTECTION ACT (PDPA)", bold: true },
                ],
                fillColor: "#9e9e9e",
                marginLeft: 5
              }
            ],
            [
              {
                ul: [
                  { text: `I consent to the clinic collecting, using, and processing my identification information, contact information, health information (sensitive personal data), and biological samples for the purposes of providing laboratory services, preparing test reports and interpretation materials, improving service quality (using anonymized data only), and complying with applicable laws.`, alignment: "justify" },
                  { text: `Personal data will be retained for at least 5 years from the date of the last service, or for the period required by law, whichever is longer.`, alignment: "justify" },
                  { text: `I have the right to access, request a copy of, rectify, erase, restrict the use of, and withdraw my consent regarding my personal data at any time. Withdrawal of consent shall not affect any processing carried out prior to the withdrawal. I also have the right to lodge a complaint with the Office of the Personal Data Protection Committee (PDPC).`, alignment: "justify" },
                  { text: `The clinic will not disclose my personal data or health information to third parties without my consent, including physicians, insurance companies, employers, legal guardians, or immediate family members, unless there is a lawful basis for such disclosure, such as public health interests, a court order, or a life-threatening emergency. The clinic will not sell my personal data to third parties under any circumstances.`, alignment: "justify" },
                ]
              }
            ]
          ]
        },
        layout: "noBorders",
      },

      // todo ---
      {
        table: {
          headerRows: 1,
          widths: ["100%"],
          body: [
            [
              {
                text: "CONSENT FOR DISCLOSURE TO THIRD PARTIES",
                bold: true,
                decoration: "underline",
                decorationStyle: "solid"
              },
            ],
            [
              {
                stack: [
                  { text: `I consent to the clinic disclosing my personal data and/or laboratory test results to the person(s) or organization(s) selected below for purposes related to healthcare, medical treatment, insurance claims, or other purposes requested by me.`, alignment: "justify" },
                  {
                    columns: [
                      { image: "unchecked", width: 10, marginTop: 2 },
                      { text: "Physician/Healthcare Facility", width: "auto" },
                      { image: "unchecked", width: 10, marginTop: 2 },
                      { text: "Insurance Company", width: "auto" },
                      { image: "unchecked", width: 10, marginTop: 2 },
                      { text: "Employer", width: "auto" },
                      { image: "unchecked", width: 10, marginTop: 2 },
                      { text: "Legal Guardian / Legal Representative", width: "auto" },
                      { image: "unchecked", width: 10, marginTop: 2 },
                      { text: `Other ${".".repeat(20)}`, width: "auto" },
                    ],
                    columnGap: 5,
                  },
                  {
                    columns: [
                      { image: "unchecked", width: 10, marginTop: 2 },
                      { text: "I do not consent to the disclosure of my personal data to third parties, except where such disclosure is required or permitted by law.", width: "auto" },
                    ],
                    columnGap: 5,
                    marginBottom: 5,
                  }
                ],
              },
            ],
          ]
        },
        layout: "noBorders",
        ...(packageItems.length > 0 ? { pageBreak: "before" } : {}),
      },

      //
      {
        text: `I acknowledge and confirm the laboratory tests specified in the test order form and consent to receive laboratory services for the tests listed therein. I further confirm that I have read, acknowledged, and agreed to be bound by all terms and conditions of service and the clinic’s Personal Data Protection Act (PDPA) as stated in this document.`,
        bold: true,
        alignment: "justify",
        marginBottom: 5,
        ...(packageItems.length === 0 ? { pageBreak: "before" } : {}),
      },

      //
      {
        stack: [
          {
            columns: [
              { text: " ", width: "*" },
              { text: "Patient / Legal Representative Signature : ", width: "auto" },
              { text: ".".repeat(100), width: "auto" },
              { text: `Date${".".repeat(20)}/${".".repeat(20)}/${".".repeat(20)}`, width: "auto" },
            ],
            alignment: "right",
            columnGap: 5,
          },
          {
            columns: [
              { text: " ", width: "*" },
              { text: "Staff Member Signature : ", width: "auto" },
              { text: ".".repeat(100), width: "auto" },
              { text: `Date${".".repeat(20)}/${".".repeat(20)}/${".".repeat(20)}`, width: "auto" },
            ],
            alignment: "right",
            columnGap: 5,
          }
        ],
      },
    ],
    footer: (currentPage: number, pageCount: number) => ({
      alignment: "right",
      marginRight: 15,
      text: `Page ${currentPage} of ${pageCount}`,
    }),
  };
};

export default FormConsentOrderPDPAEN;
